import { eventChannel } from 'redux-saga';
import { call, put, take, takeLatest } from 'redux-saga/effects';
import type { Partner, PartnerLog } from '@nx/domain';
import { getOrganizerId, partnerLogRepository, partnerRepository } from '../../services/eoRepositories';
import { pushToast } from '../slices/eoUiSlice';
import {
  partnerCreateRequested,
  partnerLoadRequested,
  partnerLoaded,
  partnerLogCreateRequested,
  partnerLogsLoaded,
  partnerLogsWatchRequested,
  partnerLogoReorderRequested,
  partnerUpdateRequested,
  partnersFailed,
  partnersLoaded,
  partnersSaveSucceeded,
  partnersWatchRequested,
} from '../slices/partnersSlice';

function* watchPartners(eventId: string): Generator<unknown, void, unknown> {
  const channel = eventChannel<Partner[]>((emit) =>
    partnerRepository.watchByEvent(eventId, (items) => emit(items)),
  );
  try {
    while (true) {
      yield put(partnersLoaded((yield take(channel)) as Partner[]));
    }
  } finally {
    channel.close();
  }
}

function* watchLogs(eventId: string, partnerId: string): Generator<unknown, void, unknown> {
  const channel = eventChannel<PartnerLog[]>((emit) =>
    partnerLogRepository.watchByPartner(eventId, partnerId, (logs) => emit(logs)),
  );
  try {
    while (true) {
      yield put(partnerLogsLoaded((yield take(channel)) as PartnerLog[]));
    }
  } finally {
    channel.close();
  }
}

export function* handlePartnersWatch(action: ReturnType<typeof partnersWatchRequested>): Generator<unknown, void, unknown> {
  yield call(watchPartners, action.payload);
}

function* handlePartnerLoad(action: ReturnType<typeof partnerLoadRequested>): Generator<unknown, void, unknown> {
  try {
    const partner = (yield call(
      [partnerRepository, partnerRepository.getById],
      action.payload.eventId,
      action.payload.partnerId,
    )) as Partner | null;
    yield put(partnerLoaded(partner));
  } catch (e) {
    yield put(partnersFailed(e instanceof Error ? e.message : 'Failed'));
  }
}

export function* handleLogsWatch(action: ReturnType<typeof partnerLogsWatchRequested>): Generator<unknown, void, unknown> {
  yield call(watchLogs, action.payload.eventId, action.payload.partnerId);
}

function* handleCreate(action: ReturnType<typeof partnerCreateRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [partnerRepository, partnerRepository.create],
      action.payload.eventId,
      action.payload.input,
    );
    yield put(partnersSaveSucceeded());
    yield put(pushToast({ msg: 'eo.toast.partnerAdded', kind: 'success' }));
  } catch (e) {
    yield put(partnersFailed(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleUpdate(action: ReturnType<typeof partnerUpdateRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [partnerRepository, partnerRepository.update],
      action.payload.eventId,
      action.payload.partnerId,
      action.payload.input,
    );
    yield put(partnersSaveSucceeded());
    yield put(pushToast({ msg: 'eo.toast.partnerSaved', kind: 'success' }));
  } catch (e) {
    yield put(partnersFailed(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleLogCreate(action: ReturnType<typeof partnerLogCreateRequested>): Generator<unknown, void, unknown> {
  try {
    const uid = getOrganizerId();
    if (!uid) throw new Error('Not signed in');
    yield call(
      [partnerLogRepository, partnerLogRepository.create],
      action.payload.eventId,
      action.payload.partnerId,
      action.payload.content,
      uid,
    );
    yield put(pushToast({ msg: 'eo.toast.partnerLogAdded', kind: 'success' }));
  } catch (e) {
    yield put(pushToast({ msg: 'eo.toast.partnerLogAddFailed', kind: 'danger' }));
  }
}

function* handleLogoReorder(action: ReturnType<typeof partnerLogoReorderRequested>): Generator<unknown, void, unknown> {
  try {
    yield call(
      [partnerRepository, partnerRepository.updateLogoOrder],
      action.payload.eventId,
      action.payload.orderedIds,
    );
    yield put(pushToast({ msg: 'eo.toast.partnerLogoOrderUpdated', kind: 'success' }));
  } catch (e) {
    yield put(pushToast({ msg: 'eo.toast.partnerLogoOrderFailed', kind: 'danger' }));
  }
}

export function* partnersSaga() {
  yield takeLatest(partnersWatchRequested.type, handlePartnersWatch);
  yield takeLatest(partnerLoadRequested.type, handlePartnerLoad);
  yield takeLatest(partnerLogsWatchRequested.type, handleLogsWatch);
  yield takeLatest(partnerCreateRequested.type, handleCreate);
  yield takeLatest(partnerUpdateRequested.type, handleUpdate);
  yield takeLatest(partnerLogCreateRequested.type, handleLogCreate);
  yield takeLatest(partnerLogoReorderRequested.type, handleLogoReorder);
}
